import closelight from "@assets/navigation/close-light.svg";
import React, { ReactNode, useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";

interface SlidingPanelProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  heading?: string;
  headingSuffix?: React.ReactNode;
  headingImage?: string;
  headingColor?: string;
  showConfirm?: boolean;
  showHeader?: boolean;
  isMobile?: boolean;
  isSlider?: boolean;
  widthDesktop?: string | number | undefined;
  onClickCross?: () => void;
  showCross?: boolean;
}

const SlidingPanel: React.FC<SlidingPanelProps> = ({
  isOpen,
  onClose,
  children,
  heading = "",
  headingImage,
  headingSuffix,
  headingColor = "text-text-base",
  showConfirm = true,
  showHeader = true,
  isMobile = false,
  isSlider = false,
  showCross = true,
  widthDesktop = "100%",
  onClickCross,
}) => {
  const navigate = useNavigate();
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const isDesktop = useMediaQuery({ query: "(min-width: 641px)" });

  useEffect(() => {
    const handleResize = () => {
      console.log("Window resized, keyboard might be opening or closing");
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!isMobile) {
    // Web
    if (!isSlider)
      return (
        <div
          className={`fixed inset-0 flex justify-center items-center transition-opacity ${
            isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          style={{
            zIndex: 1000,
            backgroundColor: isOpen ? "rgba(0, 0, 0, 0.5)" : "transparent",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
        >
          <div
            className="w-full max-w-lg bg-bg-page-body rounded-lg transform transition-transform"
            style={{
              transition: "transform 0.3s ease-in-out",
              transform: isOpen ? "scale(1)" : "scale(0.9)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {showHeader ? (
              <div className="flex flex-col">
                <div className="flex flex-row justify-between rounded-t-lg p-[16px] bg-bg-base border-b border-b-border-subtle">
                  <div>
                    <span
                      className={`${headingColor} text-[16px] font-semibold`}
                    >
                      {showConfirm ? `Confirm ${heading}` : heading}
                    </span>
                  </div>
                  {showCross && (
                    <img
                      src={closelight}
                      alt="Close"
                      className="ml-2 cursor-pointer"
                      onClick={onClose}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div className="px-4" />
            )}
            {children}
          </div>
        </div>
      );

    const slidingPanelWebStyle: React.CSSProperties = {
      width: widthDesktop,
      transition: "transform 0.3s ease-in-out",
      transform: isOpen ? "translateX(0)" : "translateX(100%)",
    };

    return (
      <div
        className={`fixed inset-0 flex items-center justify-end transition-opacity ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        style={{
          zIndex: 1000,
          backgroundColor: isOpen ? "rgba(0, 0, 0, 0.5)" : "transparent",
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div
          className="h-full bg-bg-page-body transform transition-transform flex flex-col" // Added flex and flex-col
          style={slidingPanelWebStyle}
          onClick={(e) => e.stopPropagation()}
        >
          {showHeader ? (
            <div className="fixed top-0 w-full py-4 px-4 z-50 flex flex-row justify-between  bg-bg-base items-center">
              <div className="flex items-center ">
                <span
                  className="material-symbols-outlined mr-2 cursor-pointer"
                  onClick={() => {
                    onClose();
                  }}
                >
                  arrow_back_ios
                </span>
                <div className="flex flex-row items-center">
                  {headingImage && (
                    <img
                      src={headingImage}
                      alt={heading}
                      height={12}
                      width={12}
                      className="rounded-full mr-1"
                    />
                  )}
                  <span
                    className={`${headingColor} text-[16px] font-normal mr-[8px]`}
                  >
                    {heading}
                  </span>
                  {headingSuffix && <div>{headingSuffix}</div>}
                </div>
              </div>

              {showCross && (
                <img
                  src={closelight}
                  alt="Close"
                  className="ml-2 cursor-pointer"
                  onClick={
                    onClickCross !== undefined
                      ? () => {
                          onClickCross();
                        }
                      : () => {
                          navigate(-1);
                        }
                  }
                />
              )}
            </div>
          ) : (
            <div className="px-4" />
          )}

          <div className="flex-1 overflow-y-auto">{children}</div>
        </div>
      </div>
    );
  }

  // Mobile
  const panelStyle: React.CSSProperties = {
    transition: "transform 0.3s ease-in-out",
    transform: isOpen ? "translateY(0)" : "translateY(100%)",
  };

  return (
    <div
      className={`fixed inset-0 flex ${
        isDesktop
          ? "items-center justify-center" // Center it on desktop
          : "items-end" // Slide up from bottom on mobile
      } transition-opacity ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      style={{
        zIndex: 1000,
        backgroundColor: isOpen ? "rgba(0, 0, 0, 0.5)" : "transparent",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={`${
          isDesktop ? "max-w-md w-full rounded-xl" : "w-full rounded-t-2xl"
        } bg-bg-page-body transform transition-transform`}
        style={panelStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {showHeader ? (
          <div className="flex flex-col">
            <div className="flex flex-row justify-between px-4 py-4">
              <div className="flex flex-row items-center">
                {headingImage && (
                  <img
                    src={headingImage}
                    alt={heading}
                    height={24}
                    width={24}
                    className="rounded-full mr-1"
                  />
                )}
                <span
                  className={`${headingColor} text-[14px] font-semibold mr-[8px]`}
                >
                  {showConfirm ? `Confirm ${heading}` : heading}
                </span>
                {headingSuffix && <div>{headingSuffix}</div>}
              </div>
              <img
                src={closelight}
                alt="Close"
                className="ml-2 cursor-pointer"
                onClick={onClose}
              />
            </div>
            <div className="border-b border-border-subtle mx-4"></div>
          </div>
        ) : (
          <div className="px-4" />
        )}
        {children}
      </div>
    </div>
  );
};

export default SlidingPanel;
